import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { useTheme } from '@/context/ThemeContext/useTheme';
import {
  applyStyleOverrideFonts,
  clearStyleOverrideFonts,
  sanitizeStyleOverrideFontFaces,
  type StyleOverrideFontFace,
} from '@/lib/embed/styleOverrides';
import {
  applyBrandThemeTokensToRoot,
  clearBrandThemeTokensFromRoot,
  resolveBrandThemeTokens,
  sanitizeBrandThemeRecipe,
  type BrandThemeRecipeV2,
} from '@/lib/embed/brandTheme';
import {
  EMBED_PREVIEW_APPLIED_MESSAGE_TYPE,
  EMBED_PREVIEW_FAILED_MESSAGE_TYPE,
  EMBED_PREVIEW_PROTOCOL_VERSION,
  EMBED_PREVIEW_READY_MESSAGE_TYPE,
  isEmbedPreviewSnapshotMessage,
  isEmbedPreviewSnapshotEnvelopeMessage,
  type QuizWebPreviewSnapshot,
} from '@/lib/embed/previewMessaging';
import {
  sanitizeEmailSignupPreviewConfig,
  sanitizePreviewSurface,
  sanitizeSponsorshipPreviewConfig,
} from '@/lib/embed/emailSignupPreview';
import { parseQuizWebPreviewModel } from '@/lib/embed/quizWebPreviewModel';

interface UseEmbedStyleOverridePreviewOptions {
  runtimeTheme?: BrandThemeRecipeV2 | null;
  runtimeFontFaces?: StyleOverrideFontFace[];
  receiverEnabled?: boolean;
  runtimeAppearance?: 'device' | 'light' | 'dark';
  /** Load exact runtime font resources while deferring visible token changes. */
  runtimePresentationReady?: boolean;
}

interface PendingSnapshot {
  id: number;
  revision: number;
  snapshot: QuizWebPreviewSnapshot;
}

interface ReceiverState {
  current: PendingSnapshot | null;
  applied: PendingSnapshot | null;
}

interface AppliedRenderAppearance {
  appearance: 'device' | 'light' | 'dark';
  resolvedAppearance: 'light' | 'dark';
  theme: BrandThemeRecipeV2 | null;
  fontFaces: StyleOverrideFontFace[];
}

interface PreparedRenderAppearance {
  pendingId: number;
  value: AppliedRenderAppearance;
}

interface PreviewMessageTarget {
  parentWindow: Window;
  targetOrigin: string;
}

interface FailedRenderRestore {
  revision: number;
  previous: AppliedRenderAppearance;
}

type ReceiverAction =
  | { type: 'acceptLatest'; pending: PendingSnapshot }
  | { type: 'complete'; id: number }
  | { type: 'fail'; id: number };

const INITIAL_RECEIVER_STATE: ReceiverState = {
  current: null,
  applied: null,
};

function receiverReducer(state: ReceiverState, action: ReceiverAction): ReceiverState {
  if (action.type === 'acceptLatest') {
    if (
      state.current?.revision === action.pending.revision &&
      JSON.stringify(state.current.snapshot) === JSON.stringify(action.pending.snapshot)
    ) {
      return state;
    }
    return { ...state, current: action.pending };
  }

  if (state.current?.id !== action.id) return state;
  if (action.type === 'fail') {
    return {
      ...state,
      current: null,
    };
  }
  return {
    current: null,
    applied: state.current,
  };
}

function applyActiveAppearance(
  theme: BrandThemeRecipeV2 | null,
  fontFaces: StyleOverrideFontFace[],
  appearance: 'light' | 'dark',
  applyThemeTokens = true
) {
  clearBrandThemeTokensFromRoot();
  if (!theme) {
    applyStyleOverrideFonts({}, fontFaces);
    return;
  }
  const tokens = resolveBrandThemeTokens(theme, appearance);
  applyStyleOverrideFonts(tokens, fontFaces);
  if (applyThemeTokens) applyBrandThemeTokensToRoot(tokens);
}

function sanitizeSnapshot(snapshot: QuizWebPreviewSnapshot): QuizWebPreviewSnapshot | null {
  const model = parseQuizWebPreviewModel(snapshot.model);
  const previewSurface = sanitizePreviewSurface(snapshot.render.previewSurface);
  const emailSignup =
    snapshot.render.emailSignup === null
      ? null
      : sanitizeEmailSignupPreviewConfig(snapshot.render.emailSignup);
  const sponsorship =
    snapshot.render.sponsorship === null
      ? null
      : sanitizeSponsorshipPreviewConfig(snapshot.render.sponsorship);

  // The protocol guard establishes these complete-field invariants.
  if (
    !model ||
    !previewSurface ||
    (snapshot.render.emailSignup !== null && !emailSignup) ||
    (snapshot.render.sponsorship !== null && !sponsorship)
  ) {
    return null;
  }

  return {
    model,
    render: {
      theme:
        snapshot.render.theme === null ? null : sanitizeBrandThemeRecipe(snapshot.render.theme),
      fontFaces: sanitizeStyleOverrideFontFaces(snapshot.render.fontFaces),
      appearance: snapshot.render.appearance,
      leaderboardSignInEnabled: snapshot.render.leaderboardSignInEnabled,
      previewSurface,
      emailSignup,
      sponsorship,
    },
  };
}

function reportPreviewFailure(revision: number, parentWindow: Window, targetOrigin: string) {
  parentWindow.postMessage(
    {
      type: EMBED_PREVIEW_FAILED_MESSAGE_TYPE,
      protocolVersion: EMBED_PREVIEW_PROTOCOL_VERSION,
      revision,
    },
    targetOrigin
  );
}

export function useEmbedStyleOverridePreview({
  runtimeTheme,
  runtimeFontFaces,
  receiverEnabled = true,
  runtimeAppearance,
  runtimePresentationReady = true,
}: UseEmbedStyleOverridePreviewOptions) {
  const usePresentationEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
  const { setTemporaryTheme, clearTemporaryTheme, theme: resolvedTheme } = useTheme();
  const activeThemeRef = useRef<BrandThemeRecipeV2 | null>(null);
  const activeFontFacesRef = useRef<StyleOverrideFontFace[]>([]);
  const highestRevisionRef = useRef(-1);
  const highestSnapshotCanonicalRef = useRef<string | null>(null);
  const nextPendingIdRef = useRef(0);
  const lastPreparedPendingIdRef = useRef<number | null>(null);
  const contentReadyPendingIdRef = useRef<number | null>(null);
  const appliedRevisionRef = useRef(-1);
  const appliedSnapshotCanonicalRef = useRef<string | null>(null);
  const appliedRenderAppearanceRef = useRef<AppliedRenderAppearance | null>(null);
  const preparedRenderAppearanceRef = useRef<PreparedRenderAppearance | null>(null);
  const previewMessageTargetRef = useRef<PreviewMessageTarget | null>(null);
  const failedRenderRestoreRef = useRef<FailedRenderRestore | null>(null);
  const [receiverState, dispatchReceiver] = useReducer(receiverReducer, INITIAL_RECEIVER_STATE);
  const [domThemeCommitEpoch, notifyDomThemeCommit] = useReducer((epoch: number) => epoch + 1, 0);
  const pendingSnapshot = receiverState.current;
  const ownsPresentationTokens =
    receiverEnabled || runtimeTheme !== undefined || runtimeFontFaces !== undefined;

  const completePreparedPreview = useCallback((pending: PendingSnapshot) => {
    if (pending.revision !== highestRevisionRef.current) return;
    if (lastPreparedPendingIdRef.current !== pending.id) return;
    if (contentReadyPendingIdRef.current !== pending.id) return;
    const preparedRender = preparedRenderAppearanceRef.current;
    if (!preparedRender || preparedRender.pendingId !== pending.id) return;
    const messageTarget = previewMessageTargetRef.current;
    if (!messageTarget) return;

    appliedRevisionRef.current = pending.revision;
    appliedSnapshotCanonicalRef.current = JSON.stringify(pending.snapshot);
    appliedRenderAppearanceRef.current = preparedRender.value;
    preparedRenderAppearanceRef.current = null;
    messageTarget.parentWindow.postMessage(
      {
        type: EMBED_PREVIEW_APPLIED_MESSAGE_TYPE,
        protocolVersion: EMBED_PREVIEW_PROTOCOL_VERSION,
        revision: pending.revision,
      },
      messageTarget.targetOrigin
    );
    dispatchReceiver({ type: 'complete', id: pending.id });
  }, []);

  const notifyPreviewContentReady = useCallback(() => {
    const pending = pendingSnapshot;
    if (!pending || pending.revision !== highestRevisionRef.current) return;
    contentReadyPendingIdRef.current = pending.id;
    completePreparedPreview(pending);
  }, [completePreparedPreview, pendingSnapshot]);

  useEffect(() => {
    if (!runtimeAppearance) return;
    setTemporaryTheme(runtimeAppearance);
    return () => clearTemporaryTheme();
  }, [clearTemporaryTheme, runtimeAppearance, setTemporaryTheme]);

  usePresentationEffect(() => {
    if (!ownsPresentationTokens) return;
    if (typeof globalThis.document === 'undefined') return;
    const root = globalThis.document.documentElement;
    let cancelled = false;

    function reapplyActiveAppearance() {
      if (cancelled) return;
      const committedRuntimeAppearance =
        runtimePresentationReady && runtimeAppearance
          ? runtimeAppearance === 'device'
            ? globalThis.window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : runtimeAppearance
          : null;
      const appearance =
        committedRuntimeAppearance ?? (root.dataset.theme === 'dark' ? 'dark' : 'light');
      if (committedRuntimeAppearance && root.dataset.theme !== committedRuntimeAppearance) {
        root.dataset.theme = committedRuntimeAppearance;
      }
      applyActiveAppearance(
        activeThemeRef.current,
        activeFontFacesRef.current,
        appearance,
        runtimePresentationReady
      );
    }

    activeThemeRef.current = runtimeTheme ? sanitizeBrandThemeRecipe(runtimeTheme) : null;
    activeFontFacesRef.current = sanitizeStyleOverrideFontFaces(runtimeFontFaces);
    reapplyActiveAppearance();

    const themeObserver = new MutationObserver(() => {
      reapplyActiveAppearance();
      notifyDomThemeCommit();
    });
    themeObserver.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      cancelled = true;
      themeObserver.disconnect();
      clearBrandThemeTokensFromRoot();
    };
  }, [
    ownsPresentationTokens,
    runtimeAppearance,
    runtimeFontFaces,
    runtimePresentationReady,
    runtimeTheme,
  ]);

  useEffect(() => {
    if (!ownsPresentationTokens) return;
    return () => clearStyleOverrideFonts();
  }, [ownsPresentationTokens]);

  useEffect(() => {
    if (!receiverEnabled) return;
    previewMessageTargetRef.current = {
      parentWindow: window.parent,
      targetOrigin: window.location.origin,
    };
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      if (!isEmbedPreviewSnapshotEnvelopeMessage(event.data)) return;
      if (!isEmbedPreviewSnapshotMessage(event.data)) {
        reportPreviewFailure(event.data.revision, window.parent, window.location.origin);
        return;
      }

      const sanitizedSnapshot = sanitizeSnapshot(event.data.snapshot);
      if (!sanitizedSnapshot) {
        reportPreviewFailure(event.data.revision, window.parent, window.location.origin);
        return;
      }
      const canonicalSnapshot = JSON.stringify(sanitizedSnapshot);
      if (event.data.revision < highestRevisionRef.current) return;
      if (
        event.data.revision === highestRevisionRef.current &&
        canonicalSnapshot !== highestSnapshotCanonicalRef.current
      ) {
        reportPreviewFailure(event.data.revision, window.parent, window.location.origin);
        return;
      }

      if (event.data.revision > highestRevisionRef.current) {
        highestRevisionRef.current = event.data.revision;
        highestSnapshotCanonicalRef.current = canonicalSnapshot;
      }

      if (
        event.data.revision === appliedRevisionRef.current &&
        canonicalSnapshot === appliedSnapshotCanonicalRef.current
      ) {
        window.parent.postMessage(
          {
            type: EMBED_PREVIEW_APPLIED_MESSAGE_TYPE,
            protocolVersion: EMBED_PREVIEW_PROTOCOL_VERSION,
            revision: event.data.revision,
          },
          window.location.origin
        );
        return;
      }

      dispatchReceiver({
        type: 'acceptLatest',
        pending: {
          id: nextPendingIdRef.current++,
          revision: event.data.revision,
          snapshot: sanitizedSnapshot,
        },
      });
    }

    window.addEventListener('message', handleMessage);
    window.parent.postMessage(
      {
        type: EMBED_PREVIEW_READY_MESSAGE_TYPE,
        protocolVersion: EMBED_PREVIEW_PROTOCOL_VERSION,
      },
      window.location.origin
    );

    return () => {
      previewMessageTargetRef.current = null;
      window.removeEventListener('message', handleMessage);
      clearTemporaryTheme();
      clearStyleOverrideFonts();
    };
  }, [clearTemporaryTheme, receiverEnabled]);

  useEffect(() => {
    const pending = pendingSnapshot;
    if (!receiverEnabled || !pending) return;

    setTemporaryTheme(pending.snapshot.render.appearance);
  }, [pendingSnapshot, receiverEnabled, setTemporaryTheme]);

  useEffect(() => {
    const pending = pendingSnapshot;
    if (!receiverEnabled || !pending || lastPreparedPendingIdRef.current === pending.id) {
      return;
    }
    if (pending.revision !== highestRevisionRef.current) return;
    if (resolvedTheme !== 'light' && resolvedTheme !== 'dark') return;
    const expectedAppearance =
      pending.snapshot.render.appearance === 'device'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : pending.snapshot.render.appearance;
    if (resolvedTheme !== expectedAppearance) return;
    if (document.documentElement.dataset.theme !== resolvedTheme) return;

    const previousTheme = activeThemeRef.current;
    const previousFontFaces = activeFontFacesRef.current;
    try {
      applyActiveAppearance(
        pending.snapshot.render.theme,
        pending.snapshot.render.fontFaces,
        resolvedTheme
      );
    } catch {
      const previousAppliedRender = appliedRenderAppearanceRef.current;
      if (previousAppliedRender) {
        activeThemeRef.current = previousAppliedRender.theme;
        activeFontFacesRef.current = previousAppliedRender.fontFaces;
        failedRenderRestoreRef.current = {
          revision: pending.revision,
          previous: previousAppliedRender,
        };
        setTemporaryTheme(previousAppliedRender.appearance);
        dispatchReceiver({ type: 'fail', id: pending.id });
        return;
      }

      try {
        applyActiveAppearance(previousTheme, previousFontFaces, resolvedTheme);
      } catch {
        // The parent still receives a terminal failure when no prior committed render exists.
      }
      reportPreviewFailure(pending.revision, window.parent, window.location.origin);
      dispatchReceiver({ type: 'fail', id: pending.id });
      return;
    }

    activeThemeRef.current = pending.snapshot.render.theme;
    activeFontFacesRef.current = pending.snapshot.render.fontFaces;
    lastPreparedPendingIdRef.current = pending.id;
    preparedRenderAppearanceRef.current = {
      pendingId: pending.id,
      value: {
        appearance: pending.snapshot.render.appearance,
        resolvedAppearance: resolvedTheme,
        theme: pending.snapshot.render.theme,
        fontFaces: pending.snapshot.render.fontFaces,
      },
    };
    completePreparedPreview(pending);
  }, [
    completePreparedPreview,
    domThemeCommitEpoch,
    pendingSnapshot,
    receiverEnabled,
    resolvedTheme,
    setTemporaryTheme,
  ]);

  useEffect(() => {
    const failedRestore = failedRenderRestoreRef.current;
    if (!failedRestore) return;
    if (resolvedTheme !== failedRestore.previous.resolvedAppearance) return;
    if (document.documentElement.dataset.theme !== failedRestore.previous.resolvedAppearance)
      return;

    try {
      applyActiveAppearance(
        failedRestore.previous.theme,
        failedRestore.previous.fontFaces,
        failedRestore.previous.resolvedAppearance
      );
    } catch {
      // The parent still receives the terminal failure if restoring the prior tokens fails.
    }
    failedRenderRestoreRef.current = null;
    reportPreviewFailure(failedRestore.revision, window.parent, window.location.origin);
  }, [domThemeCommitEpoch, pendingSnapshot, resolvedTheme]);

  const visibleSnapshot = receiverState.current?.snapshot ?? receiverState.applied?.snapshot;

  return {
    previewModel: visibleSnapshot?.model ?? null,
    previewSurface: visibleSnapshot?.render.previewSurface ?? 'question',
    previewEmailSignup: visibleSnapshot?.render.emailSignup ?? null,
    previewSponsorship: visibleSnapshot?.render.sponsorship ?? null,
    previewRenderSnapshot: visibleSnapshot?.render ?? null,
    notifyPreviewContentReady,
  };
}
